import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import generateReceiptPDF from './ReceiptGenerator.js';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../images/shop_logo.png";
import { SHOP_NAME } from '../Variables.js';

Modal.setAppElement('#root');

// Formatters (same as PettyCash)
const formatAmount = (value) => `₦${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
const formatAmount1 = (value) => `N${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
const dateFormatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const formatDisplayDate = (date) => date ? dateFormatter.format(new Date(date)) : 'N/A';
const formatDateTime = (date) => date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).replace(',', '');
const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PaymentTracking = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);

  const [newPayment, setNewPayment] = useState({
    invoiceId: '',
    amount: '',
    paymentMethod: 'Cash',
    paymentDate: new Date().toISOString().split('T')[0],
    transactionId: '',
    bankAccountId: ''
  });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [selectedPaymentForHistory, setSelectedPaymentForHistory] = useState(null);
  const [isFetchingPayments, setIsFetchingPayments] = useState(true);
  const [hasFetchedPayments, setHasFetchedPayments] = useState(false);

  // Date range for PDF
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const paymentsCollectionRef = collection(db, "customerPayments");
  const invoicesCollectionRef = collection(db, "invoices");
  const accountsCollectionRef = collection(db, "accounts");
  const customersCollectionRef = collection(db, "customers");

  useEffect(() => {
    if (user && userData) {
      fetchCustomers();
      fetchInvoices();
      fetchBankAccounts();
      fetchPayments();
    }
  }, [user, userData]);

  // SEARCH NOW INCLUDES CUSTOMER NAME
  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = payments.filter((payment) => {
      const invoice = invoices.find(i => i.id === payment.invoiceId);
      const customer = invoice ? customers.find(c => c.id === invoice.customerId) : null;
      const invoiceNum = invoice?.invoiceNumber || '';
      const customerName = customer?.name || '';

      return invoiceNum.toLowerCase().includes(lowerSearch) ||
             customerName.toLowerCase().includes(lowerSearch);
    });
    setFilteredPayments(filtered);
  }, [searchTerm, payments, invoices, customers]);

  const fetchCustomers = async () => {
    if (!user?.uid) return;
    try {
      const q = query(customersCollectionRef, where("active", "==", true));
      const data = await getDocs(q);
      setCustomers(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) { setError("Failed to fetch customers"); setShowModal(true); }
  };

  const fetchInvoices = async () => {
    if (!user?.uid) return;
    try {
      const q = query(invoicesCollectionRef);
      const data = await getDocs(q);
      setInvoices(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) { setError("Failed to fetch invoices"); setShowModal(true); }
  };

  const fetchBankAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef, where("type", "==", "Asset"));
      const data = await getDocs(q);
      setBankAccounts(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) { setError("Failed to fetch bank accounts"); setShowModal(true); }
  };

  const fetchPayments = async () => {
    if (!user?.uid) return;
    setIsFetchingPayments(true);
    try {
      const q = query(paymentsCollectionRef);
      const data = await getDocs(q);
      const paymentList = data.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        history: doc.data().history || [{ amount: doc.data().amount, date: doc.data().paymentDate, method: doc.data().paymentMethod, transactionId: doc.data().transactionId || '', bankAccountId: doc.data().bankAccountId || '', timestamp: doc.data().createdAt || Timestamp.now() }]
      }));
      setPayments(paymentList);
      setHasFetchedPayments(true);
    } catch (err) { setError("Failed to fetch payments"); setShowModal(true); }
    finally { setIsFetchingPayments(false); }
  };

  const getExistingPayment = (invoiceId) => payments.find(p => p.invoiceId === invoiceId);
  const getPaidAmount = (invoiceId) => {
    const p = getExistingPayment(invoiceId);
    return p ? p.history.reduce((sum, h) => sum + Number(h.amount), 0) : 0;
  };

  const getBalance = () => {
    if (!newPayment.invoiceId) return { total: 0, alreadyPaid: 0, balance: 0 };
    const invoice = invoices.find(i => i.id === newPayment.invoiceId);
    if (!invoice) return { total: 0, alreadyPaid: 0, balance: 0 };
    const total = Number(invoice.total) || 0;
    const alreadyPaid = getPaidAmount(newPayment.invoiceId);
    const entering = Number(newPayment.amount) || 0;
    return { total, alreadyPaid, balance: total - alreadyPaid - entering };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPayment(prev => ({ ...prev, [name]: value }));
  };

  const resetNewPayment = () => {
    setNewPayment({
      invoiceId: '',
      amount: '',
      paymentMethod: 'Cash',
      paymentDate: new Date().toISOString().split('T')[0],
      transactionId: '',
      bankAccountId: ''
    });
  };

  const handleRecordOrUpdatePayment = async () => {
    if (!newPayment.invoiceId || !newPayment.amount || Number(newPayment.amount) <= 0 || !newPayment.paymentDate) {
      setError("All fields are required"); setShowModal(true); return;
    }
    const balanceInfo = getBalance();
    if (balanceInfo.balance < 0) {
      setError("Amount exceeds remaining balance"); setShowModal(true); return;
    }

    setUpdate(true);
    const batch = writeBatch(db);

    try {
      const invoice = invoices.find(i => i.id === newPayment.invoiceId);
      const existingPayment = getExistingPayment(newPayment.invoiceId);

      const newHistoryEntry = {
        amount: Number(newPayment.amount),
        date: newPayment.paymentDate,
        method: newPayment.paymentMethod,
        transactionId: newPayment.transactionId || '',
        bankAccountId: newPayment.bankAccountId || '',
        timestamp: Timestamp.now()
      };

      let paymentRef;
      if (existingPayment) {
        paymentRef = doc(db, "customerPayments", existingPayment.id);
        const updatedHistory = [...existingPayment.history, newHistoryEntry];
        batch.update(paymentRef, {
          history: updatedHistory,
          amount: existingPayment.amount + Number(newPayment.amount),
          paymentMethod: newPayment.paymentMethod,
          paymentDate: newPayment.paymentDate,
          updatedAt: Timestamp.now()
        });
      } else {
        paymentRef = doc(paymentsCollectionRef);
        batch.set(paymentRef, {
          invoiceId: newPayment.invoiceId,
          amount: Number(newPayment.amount),
          history: [newHistoryEntry],
          paymentMethod: newPayment.paymentMethod,
          paymentDate: newPayment.paymentDate,
          transactionId: newPayment.transactionId || '',
          bankAccountId: newPayment.bankAccountId || '',
          userId: user.uid,
          createdAt: Timestamp.now()
        });
      }

      const totalPaid = getPaidAmount(newPayment.invoiceId) + Number(newPayment.amount);
      const invoiceRef = doc(db, "invoices", newPayment.invoiceId);
      batch.update(invoiceRef, { status: totalPaid >= Number(invoice.total) ? 'Paid' : 'Sent' });

      await batch.commit();
      setSuccessMessage("Payment recorded successfully");
      await fetchInvoices();
      await fetchPayments();
      resetNewPayment();
      setIsAddModalOpen(false);
    } catch (err) {
      setError("Failed to save payment"); console.error(err);
    } finally {
      setUpdate(false); setShowModal(true);
    }
  };

  const openAddPayment = (invoiceId) => {
    setNewPayment(prev => ({ ...prev, invoiceId }));
    setIsAddModalOpen(true);
  };

  const openHistory = async (payment) => {
    setSelectedPaymentForHistory(payment);
    setPaymentHistory(payment.history.sort((a, b) => b.timestamp - a.timestamp));
    setIsHistoryModalOpen(true);
  };

  const printPaymentReceipt = (historyEntry, invoice) => {
    const customer = customers.find(c => c.id === invoice.customerId) || {};
    const cart = [{
      name: `Invoice #${invoice.invoiceNumber}`,
      quantity: 1,
      price: historyEntry.amount,
      subtotal: historyEntry.amount
    }];

    try {
      const doc = generateReceiptPDF(
        cart,
        user,
        userData?.firstName || '',
        userData?.lastName || '',
        customer.name || '',
        customer.phone || ''
      );
      doc.text(`Method: ${historyEntry.method} - ${formatDisplayDate(historyEntry.date)}`, 40, 92);
      if (historyEntry.transactionId) doc.text(`Ref: ${historyEntry.transactionId}`, 40, 95);
      doc.save(`receipt_${invoice.invoiceNumber}_payment_${historyEntry.timestamp.toDate().getTime()}.pdf`);
    } catch (err) {
      setError("Failed to generate receipt"); setShowModal(true);
    }
  };

  // PDF GENERATION (same as PettyCash)
  const validateDateRange = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates"); setShowModal(true); return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date"); setShowModal(true); return false;
    }
    return true;
  };

  const generatePaymentsPDF = () => {
    if (!validateDateRange()) return;

    // Flatten all payment entries within date range
    const allEntries = [];
    payments.forEach(payment => {
      const invoice = invoices.find(i => i.id === payment.invoiceId);
      const customer = invoice ? customers.find(c => c.id === invoice.customerId) : null;
      payment.history.forEach(entry => {
        const entryDate = new Date(entry.date);
        if (entryDate >= new Date(startDate) && entryDate <= new Date(endDate)) {
          allEntries.push({
            ...entry,
            invoiceNumber: invoice?.invoiceNumber || 'N/A',
            customerName: customer?.name || 'N/A',
            invoiceTotal: invoice?.total || 0
          });
        }
      });
    });

    if (allEntries.length === 0) {
      setError("No payments found in selected date range"); setShowModal(true); return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : user?.displayName || "Unknown";

    let y = 10;
    doc.addImage(Logo, "PNG", 14, y, 15, 15);
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text(SHOP_NAME, 35, y + 5);
    doc.setFontSize(16); doc.setFont("helvetica", "normal");
    doc.text("Invoice Payments Report", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, y + 28);
    y += 33;

    const sorted = allEntries.sort((a, b) => new Date(a.date) - new Date(b.date));
    const tableData = sorted.map((e, i) => [
      (i + 1).toString(),
      formatDisplayDate(e.date),
      e.invoiceNumber,
      e.customerName,
      e.method,
      formatAmount1(e.amount),
      e.transactionId || '-'
    ]);

    autoTable(doc, {
      startY: y,
      head: [['S/N', 'Date', 'Invoice', 'Customer', 'Method', 'Amount', 'Ref/ID']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 14, right: 10 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25 },
        3: { cellWidth: 40 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 30 }
      }
    });

    const total = sorted.reduce((sum, e) => sum + Number(e.amount), 0);
    doc.setFontSize(12);
    doc.text(`Total Payments: ${formatAmount1(total)}`, 14, doc.lastAutoTable.finalY + 10);

    doc.save(`payments_report_${formatDateForFilename(new Date(startDate))}_to_${formatDateForFilename(new Date(endDate))}.pdf`);
    setIsDateRangeModalOpen(false);
    setStartDate(''); setEndDate('');
  };

  if (loading) return <Loader text="Loading" />;
  if (!userData) return <Loader text="Reading" />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingPayments && !hasFetchedPayments) return <p className="loading-text">Loading payments...</p>;
    if (filteredPayments.length === 0) {
      return <p className="loading-text">No payments found. Record your first payment.</p>;
    }

    return filteredPayments.map((paymentItem) => {
      const invoice = invoices.find(i => i.id === paymentItem.invoiceId);
      if (!invoice) return null;
      const total = Number(invoice.total) || 0;
      const paid = paymentItem.history.reduce((sum, h) => sum + Number(h.amount), 0);
      const balance = total - paid;

      return (
        <div key={paymentItem.id} className="class-item">
          <div className="column gap-1">
            <p>
              <strong>Invoice: {invoice.invoiceNumber}</strong><br />
              <span>Customer: {customers.find(c => c.id === invoice.customerId)?.name || 'N/A'}</span><br />
              <span>Total: {formatAmount(total)}</span><br />
              <span>Paid: {formatAmount(paid)}</span><br />
              <span className={`bold ${balance <= 0 ? 'font-green' : 'font-red'}`}>
                Bal: {formatAmount(balance)}
              </span>
            </p>
          </div>
          <div className="row align-center justify-center gap-1">
            {balance > 0 && (
              <button className="list-btn" onClick={() => openAddPayment(paymentItem.invoiceId)}>
                <i className="fas fa-money-bill"></i>
              </button>
            )}
            <button className="list-btn" onClick={() => openHistory(paymentItem)}>
              <i className="fas fa-history"></i>
            </button>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      {update && <Loader text="Processing" />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link"><i className="fa-solid fa-arrow-left size3"></i></Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Payment Tracking</h2>
            <Link to="#" className="home-link" onClick={fetchPayments}><i className="fa-solid fa-rotate-right size4"></i></Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Payment</button>
              <button className="login-button login-button-d caps" onClick={() => setIsDateRangeModalOpen(true)}>Download</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by invoice number or customer name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Payment Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => { setIsAddModalOpen(false); resetNewPayment(); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>{getExistingPayment(newPayment.invoiceId) ? "Add More Payment" : "Record Payment"}</h2>
        <form className="session-form modal-scroll" onSubmit={(e) => { e.preventDefault(); handleRecordOrUpdatePayment(); }}>
          <label className="input-label">Invoice</label>
          <select name="invoiceId" className="input-field" value={newPayment.invoiceId} onChange={handleInputChange} required>
            <option value="">Select Invoice</option>
            {invoices.filter(inv => inv.status !== 'Paid').map(invoice => (
              <option key={invoice.id} value={invoice.id}>
                {invoice.invoiceNumber} - {customers.find(c => c.id === invoice.customerId)?.name} (Bal: {formatAmount(Number(invoice.total) - getPaidAmount(invoice.id))})
              </option>
            ))}
          </select>

          {newPayment.invoiceId && (
            <>
              <div className="row justify-between"><strong>Total:</strong> <span>{formatAmount(getBalance().total)}</span></div>
              <div className="row justify-between"><strong>Paid:</strong> <span>{formatAmount(getBalance().alreadyPaid)}</span></div>
              <label className="input-label">Amount</label>
              <input type="number" step="0.01" name="amount" className="input-field" value={newPayment.amount} onChange={handleInputChange} required />
              {newPayment.amount && (
                <div className="row justify-between" style={{ color: getBalance().balance <= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                  <strong>New Balance:</strong> <span>{formatAmount(getBalance().balance)}</span>
                </div>
              )}
            </>
          )}

          <label className="input-label">Method</label>
          <select name="paymentMethod" className="input-field" value={newPayment.paymentMethod} onChange={handleInputChange}>
            <option value="Cash">Cash</option>
            <option value="Check">Check</option>
            <option value="Bank Transfer">Bank Transfer</option>
          </select>

          <label className="input-label">Date</label>
          <input type="date" name="paymentDate" className="input-field" value={newPayment.paymentDate} onChange={handleInputChange} required />

          {newPayment.paymentMethod === 'Bank Transfer' && (
            <>
              <label className="input-label">Bank Account</label>
              <select name="bankAccountId" className="input-field" value={newPayment.bankAccountId} onChange={handleInputChange} required>
                <option value="">Select Account</option>
                {bankAccounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
              </select>
            </>
          )}

          <div className="row justify-center gap-1 mt-20">
            <button type="button" className="login-button" onClick={() => { setIsAddModalOpen(false); resetNewPayment(); }}>Cancel</button>
            <button type="submit" className="login-button">Save Payment</button>
          </div>
        </form>
      </Modal>

      {/* History Modal */}
      <Modal isOpen={isHistoryModalOpen} onRequestClose={() => setIsHistoryModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Payment History</h2>
        {selectedPaymentForHistory && (
          <div className="modal-scroll">
            <p><strong>Invoice:</strong> {invoices.find(i => i.id === selectedPaymentForHistory.invoiceId)?.invoiceNumber}</p>
            {paymentHistory.map((entry, idx) => {
              const invoice = invoices.find(i => i.id === selectedPaymentForHistory.invoiceId);
              return (
                <div key={idx} className="history-entry row space-between align-center" style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
                  <div>
                    <strong>{formatAmount(entry.amount)}</strong> - {entry.method}<br />
                    <small className="font-light-blue-cash bold">{formatDisplayDate(entry.date)}</small>
                    {entry.transactionId && <small> | Ref: {entry.transactionId}</small>}
                  </div>
                  <button className="list-btn" onClick={() => printPaymentReceipt(entry, invoice)}>
                    <i className="fas fa-print"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <button className="login-button" onClick={() => setIsHistoryModalOpen(false)}>Close</button>
      </Modal>

      {/* Date Range Modal for PDF */}
      <Modal isOpen={isDateRangeModalOpen} onRequestClose={() => { setIsDateRangeModalOpen(false); setStartDate(''); setEndDate(''); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Select Date Range for Payments Report</h2>
        <form className="session-form">
          <label className="input-label">Start Date</label>
          <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          <label className="input-label">End Date</label>
          <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
          <div className="row align-center gap-1 mt-20">
            <button type="button" className="login-button" onClick={() => { setIsDateRangeModalOpen(false); setStartDate(''); setEndDate(''); }}>Cancel</button>
            <button type="button" className="login-button" onClick={generatePaymentsPDF}>Generate PDF</button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default PaymentTracking;